import type { Tone } from '../../../ui/v2';

/**
 * Store Info V2 — domain model.
 *
 * Types mirror the payloads the production Store Info page already consumes
 * (`/api/store-info/`, `/api/store-hours/`, `/api/special-hours/`, the agent
 * voice endpoints, the OpenTable dashboard endpoints and the notification
 * endpoints). Field names are the backend's; UI labels are rendered from them
 * here so the components never invent vocabulary.
 */

// ---------------------------------------------------------------------------
// Sections / IA
// ---------------------------------------------------------------------------

export type Section =
  | 'overview'
  | 'voice'
  | 'hours'
  | 'events'
  | 'reservations'
  | 'notifications'
  | 'knowledge-base'
  | 'team';

export const SECTIONS: Array<{
  key: Section;
  label: string;
  description: string;
  ownerOnly?: boolean;
  /** Shown only while Knowledge questions are on for the store. */
  knowledgeQuestionsOnly?: boolean;
}> = [
  {
    key: 'overview',
    label: 'Overview',
    description: 'What Arthur knows about this location',
  },
  {
    key: 'voice',
    label: 'Voice & greeting',
    description: 'Agent voice and call intro',
  },
  {
    key: 'hours',
    label: 'Hours',
    description: 'Weekly schedule and special dates',
  },
  {
    key: 'events',
    label: 'Events & catering',
    description: 'Requests Arthur takes and what the agent asks',
  },
  {
    key: 'reservations',
    label: 'Reservations',
    description: 'OpenTable area blackouts',
  },
  {
    key: 'notifications',
    label: 'Notifications',
    description: 'Who gets an email or text',
  },
  {
    key: 'knowledge-base',
    label: 'Knowledge Base',
    description: 'What your agent knows about your store',
    knowledgeQuestionsOnly: true,
  },
  {
    key: 'team',
    label: 'Team',
    description: 'Who can sign in and what they manage',
    ownerOnly: true,
  },
];

/** Legacy `?tab=` values → V2 section routes (kept so old links resolve). */
export const LEGACY_TAB_TO_SECTION: Record<string, Section> = {
  overview: 'overview',
  voice: 'voice',
  hours: 'hours',
  inquiries: 'events',
  reservations: 'reservations',
  notifications: 'notifications',
  team: 'team',
};

// ---------------------------------------------------------------------------
// Store info (GET /api/store-info/)
// ---------------------------------------------------------------------------

export interface RegularHour {
  day: string;
  status: string;
  hours: string;
}

export type BusyStatus = 'ONLINE' | 'BUSY' | 'PAUSED';

export interface StoreInfoData {
  store_id: number;
  /** Phonetic name the agent says. Pages show `name` instead. */
  store_name: string;
  /** Written restaurant name. */
  name?: string | null;
  phonetic_name?: string | null;
  call_intro_sentence?: string | null;
  use_default_call_intro_sentence?: boolean | null;
  default_call_intro_sentence?: string | null;
  takes_events?: boolean | null;
  event_inquiry_questions?: string[] | null;
  takes_catering?: boolean | null;
  catering_inquiry_questions?: string[] | null;
  website: string | null;
  store_address: string | null;
  address: string | null;
  how_to_order_delivery_message: string | null;
  takes_reservations: boolean;
  busy_mode_status: BusyStatus | string | null;
  busy_mode_delay_minutes: number | null;
  outdoor_seating: boolean;
  parking_info: string | null;
  location_primary_phone_number: string | null;
  location_secondary_phone_number: string | null;
  regular_hours: RegularHour[];
  today_local_date: string;
  today_day_of_week: string;
}

/** The name on the sign. `store_name` is how the agent pronounces it. */
export function restaurantName(
  store: Pick<StoreInfoData, 'name' | 'store_name'>,
): string {
  const written = store.name?.trim();
  return written || store.store_name;
}

export const BUSY_STATUS: Record<BusyStatus, { label: string; tone: Tone }> = {
  ONLINE: { label: 'Online', tone: 'success' },
  BUSY: { label: 'Busy', tone: 'warning' },
  PAUSED: { label: 'Paused', tone: 'danger' },
};

export function busyStatus(
  value: string | null | undefined,
): { label: string; tone: Tone } | null {
  if (!value) return null;
  const key = value.toUpperCase() as BusyStatus;
  return BUSY_STATUS[key] ?? { label: value, tone: 'neutral' };
}

export const MAX_CUSTOM_QUESTIONS = 7;

export function defaultCallIntroSentence(name?: string | null): string {
  const displayName = name?.trim() || 'the store';
  return `Hello, this is ${displayName}. How may I assist you today?`;
}

export function normalizeQuestions(questions: unknown): string[] {
  if (!Array.isArray(questions)) return [];
  return questions
    .filter((q): q is string => typeof q === 'string')
    .map((q) => q.trim())
    .filter(Boolean)
    .slice(0, MAX_CUSTOM_QUESTIONS);
}

export function ordinal(n: number): string {
  const words = [
    'first',
    'second',
    'third',
    'fourth',
    'fifth',
    'sixth',
    'seventh',
  ];
  return words[n - 1] ?? `#${n}`;
}

export function displayWebsite(url: string): string {
  return url
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/$/, '');
}

// ---------------------------------------------------------------------------
// Hours (GET/PUT/PATCH /api/store-hours/, /api/special-hours/)
// ---------------------------------------------------------------------------

export interface StoreHour {
  id?: number;
  day_of_week: string;
  meal_period: string;
  opening_time: string | null; // "HH:MM:SS"
  closing_time: string | null;
  is_closed: boolean;
}

export interface StoreHoursData {
  hours_source: 'manual' | 'pos' | 'google' | string;
  use_manual_hours?: boolean;
  hours: StoreHour[];
  /** The Google listing the hours were copied from, until someone edits them. */
  from_google?: {
    name: string;
    formatted_address: string;
    maps_url: string;
  } | null;
}

export interface WeeklyHoursDraft {
  day_of_week: string;
  meal_period: string;
  is_closed: boolean;
  opening_time: string; // "HH:MM"
  closing_time: string;
}

export interface SpecialHour {
  id?: number;
  date: string; // YYYY-MM-DD
  is_closed: boolean;
  holiday_name: string | null;
  special_info: string | null;
  opening_time: string | null;
  closing_time: string | null;
}

export const DAY_ORDER = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];
export const DEFAULT_MEAL_PERIOD = 'ALL_DAY';

/** "14:30:00" | "14:30" → "14:30" */
export function normalizeTime(t: string | null | undefined): string {
  if (!t) return '';
  return t.substring(0, 5);
}

/** "14:30" → "2:30 PM" */
export function to12h(t: string): string {
  if (!t) return '';
  const [hStr, m] = t.split(':');
  let h = parseInt(hStr, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  if (h > 12) h -= 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${ampm}`;
}

/** "2026-12-25" → "Dec 25, 2026" */
export function formatDate(
  d: string,
  opts: { weekday?: boolean; year?: boolean } = {},
): string {
  const date = new Date(`${d}T00:00:00`);
  return date.toLocaleDateString('en-US', {
    weekday: opts.weekday ? 'short' : undefined,
    month: 'short',
    day: 'numeric',
    year: opts.year === false ? undefined : 'numeric',
  });
}

export function toWeeklyDraft(hours: StoreHour[]): WeeklyHoursDraft[] {
  const byDay = new Map(hours.map((h) => [h.day_of_week, h]));
  return DAY_ORDER.map((day) => {
    const h = byDay.get(day);
    return {
      day_of_week: day,
      meal_period: h?.meal_period || DEFAULT_MEAL_PERIOD,
      is_closed: h?.is_closed ?? true,
      opening_time: normalizeTime(h?.opening_time),
      closing_time: normalizeTime(h?.closing_time),
    };
  });
}

export function toStoreHourPayload(row: WeeklyHoursDraft): StoreHour {
  return {
    day_of_week: row.day_of_week,
    meal_period: row.meal_period || DEFAULT_MEAL_PERIOD,
    is_closed: row.is_closed,
    opening_time: row.is_closed ? null : `${row.opening_time}:00`,
    closing_time: row.is_closed ? null : `${row.closing_time}:00`,
  };
}

export function hoursSourceLabel(
  source: string | undefined,
  manual: boolean,
): string {
  if (manual) return 'Manual';
  if (source?.toLowerCase() === 'google') return 'Google';
  return 'POS';
}

// ---------------------------------------------------------------------------
// Agent voice (GET /api/store/agent-voices/, GET/PATCH /api/store/agent-voice/)
// ---------------------------------------------------------------------------

export interface VoiceOption {
  voice_id: string;
  alias: string;
  source_name?: string;
  gender: string;
  accent: string;
  age: string;
  voice_type: string;
  traits: string[];
  /** Hosted sample. In demo fixtures, `demo:<hz>` synthesises a short tone. */
  preview_audio_url: string | null;
  avatar_url: string | null;
  recommended: boolean;
}

export type VoiceSelectionStatus =
  | 'default'
  | 'selected'
  | 'mixed'
  | 'unavailable'
  | 'unrecognized';

export interface VoiceSelection {
  status: VoiceSelectionStatus;
  voice_id: string | null;
  agent_voice_id: string | null;
  alias: string | null;
  source_name?: string | null;
  assistant_count: number;
  catalog_available: boolean;
  can_edit: boolean;
}

export function voiceLabel(
  v: Pick<VoiceOption, 'alias' | 'source_name'>,
): string {
  return v.source_name ? `${v.alias} (${v.source_name})` : v.alias;
}

export function voiceSearchText(v: VoiceOption): string {
  return [v.alias, v.source_name, ...v.traits]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export const VOICE_STATUS_NOTE: Partial<Record<VoiceSelectionStatus, string>> =
  {
    unavailable:
      "No active assistant is assigned to this store yet, so a voice can't be saved.",
    mixed:
      "This store's phone lines currently use different voices. Saving applies one voice to all of them.",
    unrecognized:
      'The current voice is outside the catalog. Pick a voice to replace it.',
  };

// ---------------------------------------------------------------------------
// Google place (platform admin) — GET/PUT /api/store/google-place/, POST …/search/
// ---------------------------------------------------------------------------

export interface GooglePlaceLink {
  place_id: string;
  name: string;
  formatted_address: string;
  maps_url: string;
}

// ---------------------------------------------------------------------------
// OpenTable area blackouts (dashboard endpoints)
// ---------------------------------------------------------------------------

export interface DiningArea {
  id: number;
  area_id: number;
  name?: string | null;
  environment?: string | null;
  table_types?: string[];
  last_seen_at?: string;
}

export interface AreaBlackout {
  id: number;
  dining_area_id: number;
  dining_area_name?: string | null;
  dining_area_environment?: string | null;
  starts_at_local: string; // "YYYY-MM-DDTHH:MM:SS"
  ends_at_local: string;
  all_day?: boolean;
  reason: string;
}

export interface ListResponse<T> {
  has_active_connection?: boolean;
  results?: T[];
}

export interface BlackoutDraft {
  id?: number;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  all_day: boolean;
  dining_area_ids: number[];
  reason: string;
}

const pad = (n: number) => n.toString().padStart(2, '0');
export const localDate = (v: string) => v.slice(0, 10);
export const localTime = (v: string) => v.slice(11, 16);
export const formatInputDate = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const formatInputTime = (d: Date) =>
  `${pad(d.getHours())}:${pad(d.getMinutes())}`;

export function addDays(date: string, days: number): string {
  const v = new Date(`${date}T00:00:00`);
  v.setDate(v.getDate() + days);
  return formatInputDate(v);
}

export const combineLocalDateTime = (date: string, time: string) =>
  `${date}T${time}:00`;

export function emptyBlackoutDraft(
  today: string,
  overrides: Partial<BlackoutDraft> = {},
): BlackoutDraft {
  return {
    start_date: today,
    end_date: today,
    start_time: '18:00',
    end_time: '21:00',
    all_day: false,
    dining_area_ids: [],
    reason: '',
    ...overrides,
  };
}

export function areaDisplayName(
  areaId: number,
  areas: DiningArea[],
  fallback?: string | null,
): string {
  return (
    fallback?.trim() ||
    areas.find((a) => a.area_id === areaId)?.name?.trim() ||
    `Area ${areaId}`
  );
}

export function isAllDayBlackout(b: AreaBlackout): boolean {
  if (typeof b.all_day === 'boolean') return b.all_day;
  return (
    localTime(b.starts_at_local) === '00:00' &&
    localTime(b.ends_at_local) === '00:00'
  );
}

/** All-day ranges end *before* this date (exclusive), like the calendar. */
export function allDayEndDateExclusive(b: AreaBlackout): string {
  const start = localDate(b.starts_at_local);
  const end = localDate(b.ends_at_local);
  return end <= start ? addDays(start, 1) : end;
}

export function formatDateRangeLabel(
  startDate: string,
  endDateExclusive: string,
): string {
  const last = addDays(endDateExclusive, -1);
  if (startDate === last) return formatDate(startDate);
  return `${formatDate(startDate, { year: false })} – ${formatDate(last)}`;
}

export function formatBlackoutWindow(b: AreaBlackout): string {
  const startDate = localDate(b.starts_at_local);
  if (isAllDayBlackout(b))
    return `${formatDateRangeLabel(startDate, allDayEndDateExclusive(b))}, all day`;
  const endDate = localDate(b.ends_at_local);
  const startTime = to12h(localTime(b.starts_at_local));
  const endTime = to12h(localTime(b.ends_at_local));
  if (startDate === endDate)
    return `${formatDate(startDate)}, ${startTime} – ${endTime}`;
  return `${formatDate(startDate, { year: false })} ${startTime} – ${formatDate(endDate)} ${endTime}`;
}

/** The sentence Arthur may say; shown live in the blackout editor. */
export function blackoutPreview(
  draft: BlackoutDraft,
  areas: DiningArea[],
): string {
  const area =
    draft.dining_area_ids.length === 0
      ? 'The area'
      : draft.dining_area_ids.length === 1
        ? areaDisplayName(draft.dining_area_ids[0], areas)
        : `${areaDisplayName(draft.dining_area_ids[0], areas)} and ${draft.dining_area_ids.length - 1} more`;
  const window = draft.all_day
    ? `all day ${formatDateRangeLabel(draft.start_date, draft.end_date)}`
    : draft.start_date === draft.end_date
      ? `from ${to12h(draft.start_time)} to ${to12h(draft.end_time)} on ${formatDate(draft.start_date)}`
      : `from ${formatDate(draft.start_date, { year: false })} ${to12h(draft.start_time)} to ${formatDate(draft.end_date)} ${to12h(draft.end_time)}`;
  return `${area} is unavailable ${window} for ${draft.reason.trim() || 'a private event'}.`;
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export type Channel = 'email' | 'sms';
export const CHANNEL_LABEL: Record<Channel, string> = {
  email: 'Email',
  sms: 'Text',
};

export interface NotificationTarget {
  id: number;
  store: number;
  channel: Channel;
  email: string | null;
  phone_e164: string | null;
  label: string;
  is_enabled: boolean;
}

export interface NotificationRuleTarget {
  id?: number;
  channel: Channel;
  email: string | null;
  phone_e164: string | null;
  label?: string | null;
  is_enabled?: boolean;
}

export interface NotificationRule {
  id: number;
  store: number;
  event_type: string;
  channel: Channel;
  is_enabled: boolean;
  category_label: string;
  category_description: string;
  additional_context: string;
  routing_priority: number;
  applies_to_all_voicemails?: boolean;
  targets?: NotificationRuleTarget[];
}

export interface EventTypeOption {
  value: string;
  label: string;
}

export const STAFF_MESSAGE = 'staff_message';
export const ALL_EVENTS = 'all_events';
export const EVENT_INQUIRY = 'event-inquiry';
export const CATERING_INQUIRY = 'catering-inquiry';
export const NEW_CONTACT = '__new__';

export interface SubscriptionRow {
  target: NotificationTarget;
  events: Array<{ rule: NotificationRule; label: string }>;
}

export function contactValue(t: {
  channel: Channel;
  email: string | null;
  phone_e164: string | null;
}): string {
  return t.channel === 'sms' ? t.phone_e164 || '' : t.email || '';
}

export function contactLabel(t: {
  label?: string | null;
  channel: Channel;
  email: string | null;
  phone_e164: string | null;
}): string {
  const name = t.label?.trim() || 'Untitled';
  const value = contactValue(t);
  return value ? `${name} · ${value}` : name;
}

export function eventDisplayLabel(
  rule: NotificationRule,
  options: EventTypeOption[],
): string {
  if (rule.event_type === STAFF_MESSAGE) {
    if (rule.applies_to_all_voicemails || !rule.category_label)
      return 'Voicemails';
    return `Voicemails · ${rule.category_label}`;
  }
  return (
    options.find((o) => o.value === rule.event_type)?.label ||
    rule.event_type
      .replace(/[_-]+/g, ' ')
      .replace(/^\w/, (c) => c.toUpperCase())
  );
}

export function targetIds(rule: NotificationRule): number[] {
  return (rule.targets || [])
    .map((t) => t.id)
    .filter((id): id is number => typeof id === 'number');
}

/** Why an event can't be subscribed to right now (shown as a `Reason`). */
export function unavailableEventReason(
  eventType: string,
  takesEvents: boolean,
  takesCatering: boolean,
): string | null {
  if (eventType === EVENT_INQUIRY && !takesEvents)
    return 'Events are off for this store.';
  if (eventType === CATERING_INQUIRY && !takesCatering)
    return 'Catering is off for this store.';
  return null;
}

export function groupSubscriptions(
  rules: NotificationRule[],
  targets: NotificationTarget[],
  options: EventTypeOption[],
): SubscriptionRow[] {
  const grouped = new Map<number, SubscriptionRow>();
  rules.forEach((rule) => {
    (rule.targets || []).forEach((rt) => {
      if (!rt.id) return;
      const known = targets.find((t) => t.id === rt.id);
      const target: NotificationTarget = known || {
        id: rt.id,
        store: rule.store,
        channel: rt.channel,
        email: rt.email,
        phone_e164: rt.phone_e164,
        label: rt.label || '',
        is_enabled: rt.is_enabled !== false,
      };
      const event = { rule, label: eventDisplayLabel(rule, options) };
      const existing = grouped.get(target.id);
      if (existing) existing.events.push(event);
      else grouped.set(target.id, { target, events: [event] });
    });
  });
  return Array.from(grouped.values()).sort((a, b) =>
    a.target.label.localeCompare(b.target.label),
  );
}
